import { api } from "../../../api/apiSlice";

// Branch-wise master (handoff §9.6) - distinct from
// src/features/users/employeesApi.js, which is the Milestone-2 "link to
// employee" search picker used by Create User (different DTO, different
// permission, different tag - "MasterEmployees" here vs "Employees" there).
//
// Route key switched from {empId} (and briefly {branchId}/{empId}) to
// {recordGuid} server-side 2026-08-12 (Batch48_EmployeeBranchScopeFix.sql) -
// empId collides at real data volume (not even unique within one branch,
// e.g. BranchId=1/empId=1310 matches 5 different real people), RecordGuid is
// the only genuinely unique key. Every GetById/Update/Salaries/Promote call
// below uses recordGuid now; the old {empId} route 404s.
//
// Salary rows are denormalized, one row per deduction applied (confirmed
// live: an employee with 3 deductions has 3 salary rows sharing the same
// amount/allowance/wb/nhif/nssf/pin, differing only by slno and the single
// `deductions` name each row carries) - same full-list diff pattern as
// Deductions' slabs. An employee with zero deductions still needs at least
// one row to persist amount/allowance at all, so the form always sends one
// row minimum (deductions: null when none picked) rather than replicating
// the legacy app's quirk of silently dropping salary data with no deduction.
export const employeesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getEmployees: builder.query({
      query: (params) => ({ url: "/masters/employees", params }),
      providesTags: (result) =>
        result
          ? [
              ...result.map((e) => ({ type: "MasterEmployees", id: e.recordGuid })),
              { type: "MasterEmployees", id: "LIST" },
            ]
          : [{ type: "MasterEmployees", id: "LIST" }],
    }),
    getEmployee: builder.query({
      query: (recordGuid) => ({ url: `/masters/employees/${recordGuid}` }),
      providesTags: (result, error, recordGuid) => [{ type: "MasterEmployees", id: recordGuid }],
    }),
    // On-entry duplicate check (Batch50) - branchId+empCode is branch-scoped,
    // empIdNo/empAcNo/empMobile are checked globally. Used as a lazy query
    // right before Save so the form can flag a duplicate before submitting,
    // instead of only finding out from the create/update call's error.
    checkUniqueEmployee: builder.query({
      query: (params) => ({ url: "/masters/employees/check-unique", params }),
    }),
    createEmployee: builder.mutation({
      query: (body) => ({ url: "/masters/employees", method: "POST", data: body }),
      invalidatesTags: [{ type: "MasterEmployees", id: "LIST" }],
    }),
    updateEmployee: builder.mutation({
      query: ({ recordGuid, ...body }) => ({
        url: `/masters/employees/${recordGuid}`,
        method: "PUT",
        data: body,
      }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "MasterEmployees", id: recordGuid },
        { type: "MasterEmployees", id: "LIST" },
      ],
    }),
    replaceEmployeeSalaries: builder.mutation({
      query: ({ recordGuid, salaries }) => ({
        url: `/masters/employees/${recordGuid}/salaries`,
        method: "PUT",
        data: { salaries },
      }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "MasterEmployees", id: recordGuid },
      ],
    }),
    // Designation Salary Bulk Update (Batch51, 2026-08-14) - REST port of the
    // legacy "Update Salary" panel (Frm_General_Settings.cs). Overwrites the
    // Amount on every Employee_Sallery row for every active employee holding
    // a given designation in a given branch - no undo, so the preview call
    // must always be shown to the operator before commit.
    getDesignationSalaryPreview: builder.query({
      query: ({ branchId, designationId }) => ({
        url: "/masters/employees/designation-salary/preview",
        params: { branchId, designationId },
      }),
    }),
    bulkUpdateDesignationSalary: builder.mutation({
      query: (body) => ({
        url: "/masters/employees/designation-salary/bulk-update",
        method: "POST",
        data: body,
      }),
      invalidatesTags: [{ type: "MasterEmployees", id: "LIST" }],
    }),
    // Re-codes the employee (handoff §9.6, Employees.Promote): deactivates
    // the old record, creates a brand-new one under newEmpCode carrying over
    // every other field, response is the NEW employee's detail - navigate
    // there, not back to the old record. Salary is never carried over
    // (response always has salaries: []) - a separate replaceEmployeeSalaries
    // call against the new recordGuid is needed if the new role's pay differs.
    promoteEmployee: builder.mutation({
      query: ({ recordGuid, ...body }) => ({
        url: `/masters/employees/${recordGuid}/promote`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "MasterEmployees", id: recordGuid },
        { type: "MasterEmployees", id: "LIST" },
      ],
    }),
    // Guard Remarks & Grading (Batch52, 2026-08-15) - a timeline of
    // conduct/performance notes per employee, gated by their own
    // Employees.ViewRemarks/.AddRemarks permissions (separate from general
    // Employees.View/.Edit - conduct notes are more sensitive). Grade is
    // optional per entry, one of Excellent/Good/Average/Bad; the list
    // response's CurrentGrade is the most recent non-null-graded entry.
    getEmployeeRemarks: builder.query({
      query: (recordGuid) => ({ url: `/masters/employees/${recordGuid}/remarks` }),
      providesTags: (result, error, recordGuid) => [{ type: "EmployeeRemarks", id: recordGuid }],
    }),
    addEmployeeRemark: builder.mutation({
      query: ({ recordGuid, ...body }) => ({
        url: `/masters/employees/${recordGuid}/remarks`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "EmployeeRemarks", id: recordGuid },
      ],
    }),
    // Keyword search across a branch scope (the API form of the legacy
    // Emp_History "LIKE '%theft%'" query) - joined server-side via
    // EmployeeRecordGuid so results carry the employee's name/code.
    searchEmployeeRemarks: builder.query({
      query: (params) => ({ url: "/masters/employees/remarks/search", params }),
    }),
    // Employee photo (Batch67, same physical-capture flow the Flutter
    // Attendance enroll screen uses) - GET streams the file directly, so
    // there's nothing to "query" here beyond a plain URL string the <img>
    // tag can hit itself; the only real endpoint needed on this side is the
    // presence flag already returned on the employee detail (hasPhoto).
    // No mutation for uploading a photo from React - that capture only
    // happens on the device, in Flutter.

    // Scanned document attachments (Batch68) - Employee Master screen only,
    // per explicit instruction (Flutter never touches this). View/Manage
    // permissions are split (Employees.ViewDocuments/.ManageDocuments), same
    // shape as ViewSalary/EditSalary.
    getEmployeeDocuments: builder.query({
      query: (recordGuid) => ({ url: `/masters/employees/${recordGuid}/documents` }),
      providesTags: (result, error, recordGuid) => [{ type: "EmployeeDocuments", id: recordGuid }],
    }),
    uploadEmployeeDocument: builder.mutation({
      query: ({ recordGuid, documentType, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("documentType", documentType);
        return { url: `/masters/employees/${recordGuid}/documents`, method: "POST", data: formData };
      },
      invalidatesTags: (result, error, { recordGuid }) => [{ type: "EmployeeDocuments", id: recordGuid }],
    }),
    deleteEmployeeDocument: builder.mutation({
      query: ({ recordGuid, documentRecordGuid }) => ({
        url: `/masters/employees/${recordGuid}/documents/${documentRecordGuid}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { recordGuid }) => [{ type: "EmployeeDocuments", id: recordGuid }],
    }),
    // Lazy, on-demand only (never prefetched alongside the list) - same
    // "fetch the bytes only on explicit click" discipline as Collections'
    // attachment viewer. responseType: "blob" passes straight through
    // axiosBaseQuery to axios, same as the customer QR code endpoint.
    getEmployeeDocumentContent: builder.query({
      query: ({ recordGuid, documentRecordGuid }) => ({
        url: `/masters/employees/${recordGuid}/documents/${documentRecordGuid}/content`,
        responseType: "blob",
      }),
    }),
    // An employee's "Approval Signature" document (Batch92), fetched by
    // whoever is printing a document that employee approved (LPO first) -
    // deliberately not gated by Employees.ViewDocuments on the API side,
    // so this is safe to call from any authenticated screen. 404s (no
    // signature on file) are handled by the caller, not here.
    // The General Manager's seal (2026-09-09). Quotations and Petty Cash
    // vouchers carry the GM's signature as the company's mark of authority,
    // regardless of who approved the individual document. Resolved server-side
    // from the RBAC designation on the user account, so no client here needs to
    // know who the GM is (and none of them may list users anyway).
    getGeneralManagerSignature: builder.query({
      query: () => ({ url: "/masters/employees/general-manager/signature", responseType: "blob" }),
    }),
    getEmployeeSignature: builder.query({
      query: (recordGuid) => ({
        url: `/masters/employees/${recordGuid}/signature`,
        responseType: "blob",
      }),
    }),
    // Lazy blob fetch for inline <img> display (AI Guard Roster avatars) - same
    // responseType: "blob" + object-URL pattern as getEmployeeDocumentContent/
    // getEmployeeSignature above. A bare <img src="{apiUrl}/photo"> can't attach
    // this app's bearer token, so the photo has to come through here, not a raw URL.
    getEmployeePhotoBlob: builder.query({
      query: (recordGuid) => ({
        url: `/masters/employees/${recordGuid}/photo`,
        responseType: "blob",
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetEmployeesQuery,
  useGetEmployeeQuery,
  useLazyCheckUniqueEmployeeQuery,
  useCreateEmployeeMutation,
  useUpdateEmployeeMutation,
  useReplaceEmployeeSalariesMutation,
  usePromoteEmployeeMutation,
  useLazyGetDesignationSalaryPreviewQuery,
  useBulkUpdateDesignationSalaryMutation,
  useGetEmployeeRemarksQuery,
  useAddEmployeeRemarkMutation,
  useLazySearchEmployeeRemarksQuery,
  useGetEmployeeDocumentsQuery,
  useUploadEmployeeDocumentMutation,
  useDeleteEmployeeDocumentMutation,
  useLazyGetEmployeeDocumentContentQuery,
  useLazyGetEmployeeSignatureQuery,
  useLazyGetGeneralManagerSignatureQuery,
  useLazyGetEmployeePhotoBlobQuery,
} = employeesApi;
